/**
 * Blue card rendering the raw NCCN guideline text.
 *
 * When both the AI and human verdicts are Met (signalled by the caller), a
 * copy control appears in the bottom-right of the box so the full guideline
 * text (without the title) can be pasted into the BO Notes.
 *
 * When histology and/or category values are present, an info icon next to the
 * title shows them in a hover/focus tooltip.
 * @module GuidelineTextCard
 */
import React from "react";
import { InfoIcon } from "../../../svg/info-icon";
import { GuidelineCopyButton } from "./GuidelineCopyButton";

/** Props for {@link GuidelineTextCard}. */
interface GuidelineTextCardProps {
  /** The guideline text to display. */
  text?: string | null;
  /** Optional guideline name/label shown above the text. */
  name?: string | null;
  /** Whether the AI verdict for this guideline is Met. */
  aiVerdictMet?: boolean;
  /** Whether the reviewer's human verdict for this guideline is Met. */
  humanVerdictMet?: boolean;
  /** Histology this guideline applies to; shown in the info tooltip. */
  histology?: string | null;
  /** NCCN recommendation category; shown in the info tooltip. */
  category?: string | null;
  /** Fired when the guideline text is copied to the clipboard. */
  onCopy?: () => void;
}

/** Collapsed height of the text region, expressed as a fraction of the viewport. */
const COLLAPSED_MAX_HEIGHT = "16vh";

/**
 * Renders the guideline text in a light-blue emphasis box.
 *
 * The text region is collapsed to 15% of the viewport height by default and
 * can be expanded to its full content height via a "Read more" toggle.
 *
 * @param props - Guideline text and optional name
 * @returns The rendered card, or null when there is no text
 */
export const GuidelineTextCard: React.FC<GuidelineTextCardProps> = ({
  text,
  name,
  aiVerdictMet,
  humanVerdictMet,
  histology,
  category,
  onCopy,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const textRef = React.useRef<HTMLParagraphElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;
    const collapsedHeightPx =
      (window.innerHeight * parseInt(COLLAPSED_MAX_HEIGHT, 10)) / 100;
    setIsOverflowing(element.scrollHeight > collapsedHeightPx + 1);
  }, [text]);

  if (!text) return null;

  const showToggle = isOverflowing || expanded;
  const showCopy = aiVerdictMet && humanVerdictMet;

  // Only render the info icon when the backend supplied at least one value.
  const infoRows = [
    { label: "Histology", value: histology },
    { label: "Category", value: category },
  ].filter(
    (row): row is { label: string; value: string } =>
      typeof row.value === "string" && row.value.trim().length > 0,
  );
  const hasInfo = infoRows.length > 0;

  return (
    <div className="flex flex-shrink-0 items-stretch border border-[#DDE5F5] bg-[#FAFBFF]">
      <div className="w-[4px] flex-shrink-0 self-stretch bg-[#0056D6]" />
      <div className="flex flex-1 flex-col items-start gap-[4px] px-[14px] py-[10px]">
        <div className="flex items-center gap-[6px]">
          <span className="text-[10px] font-bold uppercase leading-[15px] tracking-[0.6px] text-[#0056D6]">
            {name || "NCCN Guideline"}
          </span>
          {hasInfo && (
            <span
              tabIndex={0}
              role="button"
              aria-label={`${name || "NCCN Guideline"}: ${infoRows
                .map((row) => `${row.label} ${row.value}`)
                .join(", ")}`}
              className="group relative rounded-full outline-none focus-visible:ring-1 focus-visible:ring-[#0056D6]"
            >
              <InfoIcon width="14" height="14" stroke="#0056D6" />
              <span className="pointer-events-none absolute left-0 top-full z-20 flex w-max max-w-[280px] flex-col items-start gap-1 rounded-[6px] border border-[#E6E6E6] bg-white p-[10px] opacity-0 shadow-[0_20px_20px_-5px_rgba(0,0,0,0.10),0_8px_10px_-6px_rgba(0,0,0,0.10)] transition-opacity [font-family:Lato] group-hover:opacity-100 group-focus-visible:opacity-100">
                {infoRows.map((row) => (
                  <span
                    key={row.label}
                    className="flex items-baseline gap-[12px]"
                  >
                    <span className="text-[10px] font-bold leading-4 text-[#1F1F1F]">
                      {row.label}
                    </span>
                    <span className="text-[10px] font-normal leading-4 text-[#0F0F0F] [font-feature-settings:'ss01'_on]">
                      {row.value}
                    </span>
                  </span>
                ))}
              </span>
            </span>
          )}
        </div>
        <div className="flex items-end gap-3">
          {/* Trim trailing newlines so pre-wrap doesn't render blank lines
              under the text. */}
          <div className="relative min-w-0 flex-1">
            <p
              ref={textRef}
              className="self-stretch overflow-hidden whitespace-pre-wrap text-[12px] font-normal leading-[20px] text-[#1A1A1A]"
              style={{ maxHeight: expanded ? "none" : COLLAPSED_MAX_HEIGHT }}
            >
              {text?.trimEnd()}
            </p>
            {!expanded && isOverflowing && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#FAFBFF] to-transparent" />
            )}
          </div>
          {showCopy && (
            <GuidelineCopyButton
              text={text ?? ""}
              label="Copy guideline text"
              onCopy={onCopy}
            />
          )}
        </div>
        {showToggle && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline"
          >
            {expanded ? "Read less" : "Read more"}
          </button>
        )}
      </div>
    </div>
  );
};
